/**
 * The formats a stored transcript is rendered as, and the content-type each one
 * is served, downloaded, or attached with.
 *
 * Lives here rather than in `@kiao-voice/api-contract` because apps/api is held
 * to a ZERO runtime dependency on that package (gated by `no-restricted-imports`
 * in `apps/api/eslint.config.js`) — a shared MIME table is a runtime value, and
 * the API is one of the five callers. `@kiao-voice/core` is the shared package
 * both sides may import values from, and the formatters that PRODUCE these
 * bytes (`vtt-formatter.ts`) already live next door.
 */

/**
 * `json` is the machine-consumer shape reserved for the public batch API. The
 * user-facing download surfaces expose only the three text formats and say so
 * as `Exclude<TranscriptFormat, 'json'>` rather than by redeclaring the union,
 * so a format added here reaches them as a compile error instead of silently
 * missing from a picker.
 */
export const TRANSCRIPT_FORMATS = ['vtt', 'srt', 'json', 'text'] as const
export type TranscriptFormat = (typeof TRANSCRIPT_FORMATS)[number]

/**
 * Content-type served for each format, and the MIME stamped on the matching
 * browser-side download `Blob`.
 *
 * One table because five hand-written copies had already drifted (#636 case 3):
 * two omitted the space after the semicolon, one carried no `charset` at all,
 * and one was a nested ternary. `charset=utf-8` is not decorative — transcripts
 * are routinely 中文, and a `.vtt` served or attached without it is decoded by
 * whatever the recipient's client guesses.
 *
 * The parameter is separated with `; ` per RFC 9110's `parameters` production.
 * The OpenAPI spec documents the same media types bare, as its media-type map
 * keys require; that is the same contract, not a second one.
 */
export const TRANSCRIPT_FORMAT_MIME: Readonly<Record<TranscriptFormat, string>> = {
  vtt: 'text/vtt; charset=utf-8',
  srt: 'application/x-subrip; charset=utf-8',
  json: 'application/json; charset=utf-8',
  text: 'text/plain; charset=utf-8',
}

/**
 * Content-type for a format, or `null` when the value is not one.
 *
 * `Object.hasOwn` rather than a plain index: an index into an object literal
 * walks the prototype, so a format string of `constructor` resolves to a
 * function where the signature promises a string.
 */
export function transcriptFormatMime(format: string): string | null {
  return Object.hasOwn(TRANSCRIPT_FORMAT_MIME, format)
    ? TRANSCRIPT_FORMAT_MIME[format as TranscriptFormat]
    : null
}

/**
 * The three text formats a person downloads from the operator tools (the batch
 * history download and the outreach review export). `json` is the machine
 * shape reserved for the public batch API, so it is not one of them.
 */
export const TEXT_TRANSCRIPT_FORMATS = ['vtt', 'srt', 'text'] as const satisfies readonly Exclude<
  TranscriptFormat,
  'json'
>[]
export type TextTranscriptFormat = (typeof TEXT_TRANSCRIPT_FORMATS)[number]
